import {
    createAsyncThunk,
    createSlice,
    isPending,
    isFulfilled,
    isRejected
} from '@reduxjs/toolkit';
import { httpGet, httpPost } from '../../helpers/http.js';
import { launchTypes } from '../elearning/helpers.js';
import { buildYouTubeEmbedUrl } from './youtube.js';
import { buildVimeoEmbedUrl } from './vimeo.js';

export const previewMicrolearning = createAsyncThunk(
    'preview/microlearning',
    async (args, { getState }) => {
        const { env, auth } = getState();
        const { launchId } = args.microlearning;

        const data = await getLtiStartData({
            url: `${env.settings.ltiConsumerApi}/api/1.0/tcgStartUrl`,
            launchId: launchId,
            user: auth.user,
        });

        return {
            launchId: launchId,
            url: data.url,
            popupCanClose: true,
            popup: true,
        };
    },
);

export const previewElearning = createAsyncThunk(
    'preview/elearning',
    async (args, { getState }) => {
        const { env, auth } = getState();
        const { targetId, launchId } = args.item; // can be scorm, lti, elearning or composition item

        const targetIdOrLaunchId = launchId || targetId;
        const previewItem = await httpGet(`${env.settings.compositionApi}/elearningitems/preview/${targetIdOrLaunchId}`, auth.user);

        const {startUrl, startData, launchType, playerType, reference} = previewItem;

        switch (playerType) {
            case 0: // 'lti1':
                const port = window.location.port ? `:${window.location.port}` : '';
                const baseUrl = `${window.location.protocol}//${window.location.hostname}${port}`;

                const data = await httpPost(startUrl, args.user, {
                    uniqueId: targetIdOrLaunchId,
                    launchPresentationReturnUrl: `${baseUrl}/close-preview`,
                    reset: false, // tcgStartUrl only
                    idString: auth.user.profile.sub, // startUrl only
                    displayName: auth.user.profile.name,
                    email: auth.user.profile.email,
                });

                const overrideNewWindow = data.newWindow === true;
                const url = overrideNewWindow ? `${data.url}/close-preview?url=${encodeURIComponent(data.url)}` : data.url;

                return {
                    playerType: playerType,
                    launchId: targetIdOrLaunchId,
                    launchType: overrideNewWindow
                        ? launchTypes.LAUNCHTYPE_NEWWINDOW
                        : launchTypes.LAUNCHTYPE_REGULAR,
                    url: data.url,
                    popupCanClose: false,
                    popup: true,
                };

            case 1: // 'onlineStudio':
                return {
                    playerType: playerType,
                    launchId: targetIdOrLaunchId,
                    launchType: launchType,
                    launchData: {
                        ...startData,
                        referenceId: reference,
                    },
                    popupCanClose: true,
                    popup: true,
                };

            case 3: // 'ltiAdvantage'
                const response = await httpPost(`${previewItem.startData.launchTokenUrl}/launchtoken`, auth.user, {
                    portalIdentifier: env.portalIdentifier,
                    launchId: startData.resourceLink,
                    userName: auth.user.profile.name,
                    userEmail: auth.user.profile.email,
                });

                return {
                    playerType: playerType,
                    launchId: targetIdOrLaunchId,
                    launchType: launchType,
                    url: response.launchUrl,
                    popupCanClose: false,
                    popup: true,
                };
                break;

            case 4: // 'youtube':
                const youtubeUrl = buildYouTubeEmbedUrl(startData);
                return {
                    playerType: playerType,
                    launchId: targetIdOrLaunchId,
                    launchType: launchType,
                    launchData: startData,
                    url: youtubeUrl,
                    popupCanClose: false,
                    popup: true,
                };

            case 5: // 'vimeo':
                const vimeoUrl = buildVimeoEmbedUrl(startData);
                return {
                    playerType: playerType,
                    launchId: targetIdOrLaunchId,
                    launchType: launchType,
                    launchData: startData,
                    url: vimeoUrl,
                    popupCanClose: false,
                    popup: true,
                };

            case 2: // 'scorm12':
            case 99: // 'blackbox':
                return {
                    playerType: playerType,
                    launchId: targetIdOrLaunchId,
                    launchType: launchType,
                    launchData: startData,
                    url: startUrl,
                    popupCanClose: false,
                    popup: true,
                };
            default:
                return {};
        }
    },
);

const previewSlice = createSlice({
    name: 'preview',
    initialState: {
        status: 'idle',
        error: null
    },
    reducers: {
        closePreview: (state) => {
            state.popupOpen = false;
            state.launchId = null;
            state.launchType = launchTypes.LAUNCHTYPE_REGULAR;
            state.launchData = null;
            state.launchUrl = null;
    },
    },
    extraReducers: (builder) => {
        builder
            .addMatcher(isPending(previewMicrolearning, previewElearning), (state) => {
                state.status = 'loading';
            })
            .addMatcher(isFulfilled(previewMicrolearning, previewElearning), (state, action) => {
                const { playerType, launchId, launchType, launchData, url, popup, popupCanClose } = action.payload;

                state.status = 'success';
                state.playerType = playerType;
                state.launchId = launchId;
                state.launchType = launchType;
                state.launchData = launchData;
                state.launchUrl = url;
                state.popupOpen = popup ? true : undefined;
                state.autoClose = popup ? popupCanClose : undefined;
            })
            .addMatcher(isRejected(previewMicrolearning, previewElearning), (state, action) => {
                state.status = 'failed';
                state.error = action.error;
            });
    },
});

export const { closePreview } = previewSlice.actions;

export default previewSlice.reducer;
